import { useDrawer } from '@iveco-ti/iveco-ds'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deleteApplicationAccountUser(id: string) {
  return api.backend.delete(`/application-account-users/${id}`)
}

export function useRemoveApplicationAccountUser() {
  const { t } = useTranslation('sso')
  const { handleModal, toggleLoading } = useModalProvider()
  const drawerManageUserAccess = useDrawer(
    'drawer-manage-application-account-user',
  )

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationFn: deleteApplicationAccountUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t(
            'access.use_remove_application_account_user.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['/users-access/paginate'],
        exact: false,
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'access.use_remove_application_account_user.toast_error.title',
          ),
          description:
            error?.response?.data?.friendlyMessage ||
            t(
              'access.use_remove_application_account_user.toast_error.description',
            ),
        },
      })
    },
  })

  const handleRemoveApplicationAccountUser = async (id: string) => {
    handleModal({
      type: 'warning',
      title: t('access.use_remove_application_account_user.modal.title'),
      description: t(
        'access.use_remove_application_account_user.modal.description',
      ),
      confirmText: t(
        'access.use_remove_application_account_user.modal.button_confirm',
      ),
      onCancel: () => drawerManageUserAccess.open(),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(id).finally(() => {
          toggleLoading()
          drawerManageUserAccess.open()
        })
      },
    })
  }

  return {
    handleRemoveApplicationAccountUser,
  }
}
